import { ChevronRightIcon } from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SPACE_ACCESS } from '../features/pricing/space-access'
import { classNames } from '../helpers'
import { trackEvent } from '../helpers/analytics'
import { switchActiveSpace } from '../helpers/switch-active-space'
import Badge from './tailwind/Badge'

// Auswahl der anderen Standorte eines Nutzers, wenn der aktuelle keinen Zugang
// hat (AccessDenied und Abgelaufen-Ansicht). Ein Klick wechselt den Standort
// und lädt das Dashboard neu. Der Nutzer wählt selbst, deshalb gilt der Ziel-
// standort als bewusst gewählt (helpers/switch-active-space.js).
//
// Ein Eintrag mit access TRIAL bekommt ein Badge, damit eine Testphase neben
// einem bezahlten Standort erkennbar ist.
const SpaceChoiceList = ({
  spaces,
  userId,
  restartDashboard,
  trackingEvent
}) => {
  const { t } = useTranslation()

  const [isSwitching, setIsSwitching] = useState(false)

  const handleOnClick = ({ space_id }) => {
    if (isSwitching) return
    setIsSwitching(true)

    trackEvent(trackingEvent)

    switchActiveSpace({ userId, spaceId: space_id })
      .then(restartDashboard)
      .catch(() => setIsSwitching(false))
  }

  return (
    <div className='mx-auto mt-8 max-w-md divide-y divide-gray-100 rounded-xl bg-white text-left shadow-lg ring-1 ring-gray-900/5'>
      {spaces.map(({ id: space_id, partner, access }) => (
        <button
          key={space_id}
          type='button'
          disabled={isSwitching}
          onClick={() => handleOnClick({ space_id })}
          className={classNames(
            'relative flex w-full items-center px-3 py-2 text-left first:rounded-t-xl last:rounded-b-xl sm:gap-x-3',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brightBlue',
            isSwitching
              ? 'cursor-default opacity-50'
              : 'group cursor-pointer hover:bg-gray-50'
          )}
        >
          <img
            className='hidden h-12 w-12 flex-none rounded-lg border-2 border-gray-50 bg-gray-50 object-contain group-hover:border-gray-100 group-hover:bg-gray-100 sm:block'
            src={partner?.logo}
            alt=''
          />
          <div className='flex min-w-0 flex-col justify-center'>
            <p className='truncate text-left text-sm font-medium text-gray-900'>
              {partner?.name}
            </p>
            <span className='truncate text-left text-xs text-gray-700'>
              {partner?.display_name}
            </span>
          </div>
          <div className='ml-auto' />
          {access === SPACE_ACCESS.TRIAL && (
            <Badge
              type='basic'
              color='gray'
              text={t('access_expired.trial_badge')}
              className='ml-3 mr-2 flex-none'
            />
          )}
          <ChevronRightIcon
            className='h-5 w-5 flex-none text-gray-700 group-hover:text-gray-900 sm:mr-2'
            aria-hidden='true'
          />
        </button>
      ))}
    </div>
  )
}

SpaceChoiceList.propTypes = {
  spaces: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      partner: PropTypes.object,
      access: PropTypes.string
    })
  ).isRequired,
  userId: PropTypes.string.isRequired,
  restartDashboard: PropTypes.func.isRequired,
  trackingEvent: PropTypes.string.isRequired
}

export default SpaceChoiceList
